// @ts-nocheck
/* eslint-disable */

// Allow usage of this.$keycloak in components
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties  {
    $keycloak: VueKeycloakInstance
  }
}

import { reactive, readonly } from 'vue';
import Keycloak from 'keycloak-js';
import qs from 'qs';
import axios from 'axios';
import {
  store
} from '@piveau/piveau-hub-ui-modules';

const LOG_PREFIX = '[vue-keycloak]';
let installed = false;
let rtpToken = null;

const keycloakStore = reactive({
  ready: false,
  authenticated: false,
  userName: null,
  fullName: null,
  token: null,
  rtpToken: null,
  tokenParsed: null,
  logoutFn: null,
  loginFn: null,
  login: null,
  createLoginUrl: null,
  createLogoutUrl: null,
  createRegisterUrl: null,
  register: null,
  accountManagement: null,
  createAccountUrl: null,
  loadUserProfile: null,
  loadUserInfo: null,
  subject: null,
  idToken: null,
  idTokenParsed: null,
  realmAccess: null,
  resourceAccess: null,
  refreshToken: null,
  refreshTokenParsed: null,
  timeSkew: null,
  responseMode: null,
  responseType: null,
  hasRealmRole: null,
  hasResourceRole: null,
  getRtpToken: null,
  keycloak: null,
});

export function useKeycloak() {
  return readonly(keycloakStore);
}

export default {
  install(app, params = {}) {
    if (installed) return;
    installed = true;

    const defaultParams = {
      config: window.__BASEURL__ ? `${window.__BASEURL__}/config` : '/config',
      init: { onLoad: 'login-required' },
    };
    const options = Object.assign({}, defaultParams, params);
    if (assertOptions(options).hasError) throw new Error(`Invalid options given: ${assertOptions(options).error}`);

    app.config.globalProperties.$keycloak = readonly(keycloakStore);

    getConfig(options.config)
    .then((config) => {
      init(config, keycloakStore, options);
    })
    .catch((err) => {
      console.error(err);
    });
  },
};

async function init(config, watch, options) {
  const ctor = sanitizeConfig(config);
  const keycloak = new Keycloak(ctor);
  const enableLogging = options.init?.enableLogging;
  console.group(`${LOG_PREFIX} 🔍 DIAGNOSTIC: Keycloak init config`);
  console.log('Constructor config:', JSON.stringify(ctor));
  console.log('Init options:', JSON.stringify(options.init));
  console.log('silentCheckSsoRedirectUri:', options.init?.silentCheckSsoRedirectUri);
  console.log('checkLoginIframe:', options.init?.checkLoginIframe);
  console.log('silentCheckSsoFallback:', options.init?.silentCheckSsoFallback ?? '(not set, defaults to true)');
  console.log('pkceMethod:', options.init?.pkceMethod);
  console.log('responseMode:', options.init?.responseMode);
  console.log('Current URL:', window.location.href);
  console.log('isSecureContext:', globalThis.isSecureContext);
  console.groupEnd();
  const globalMessageListener = (event) => {
    const dominated = ['webpack', 'react-devtools', 'HMR', 'vite'];
    const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
    if (dominated.some(d => data?.includes?.(d))) return;
    console.log(`${LOG_PREFIX} 🔍 DIAGNOSTIC: postMessage received`, {
      origin: event.origin,
      data: event.data,
      sourceIsIframe: event.source !== window,
      timestamp: new Date().toISOString(),
    });
  };
  window.addEventListener('message', globalMessageListener);

  keycloak.onReady = function (authenticated) {
    updateWatchVariables(authenticated);
    watch.ready = true;
    console.log(`${LOG_PREFIX} 🔍 DIAGNOSTIC: onReady fired`, {
      authenticated,
      token: !!keycloak.token,
      refreshToken: !!keycloak.refreshToken,
      sessionId: keycloak.sessionId,
      silentCheckSsoRedirectUri: keycloak.silentCheckSsoRedirectUri,
      timestamp: new Date().toISOString(),
    });
    if (enableLogging) {
      console.log(`${LOG_PREFIX} Ready, authenticated: ${authenticated}`);
    }
    typeof options.onReady === 'function' && options.onReady(keycloak, watch);
  };

  keycloak.onAuthSuccess = function () {
    // Check token validity every 10 seconds (10 000 ms) and, if necessary, update the token.
    // Refresh token if it's valid for less then 60 seconds
    const updateTokenInterval = setInterval(() => keycloak.updateToken(60)
    .then((hasRefreshed) => {
      if (enableLogging) {
        console.log(`${LOG_PREFIX} Token ${hasRefreshed ? 'updated' : 'not updated'}`);
      }
      if (hasRefreshed) {
        // When the auth token refreshes, 'invalidate' the stored rtpToken
        // to force getting a new rtpToken the next time
        rtpToken = null;
      }
    })
    .catch((error) => {
      if (enableLogging) {
        console.log(`${LOG_PREFIX} Error while updating token: ${error}`);
      }
      rtpToken = null;
      keycloak.clearToken();
    }), 10000);

    watch.logoutFn = () => {
      clearInterval(updateTokenInterval);
      return keycloak.logout(options.logout);
    };
  };

  keycloak.onAuthRefreshSuccess = function () {
    updateWatchVariables(true);
    if (typeof options.onAuthRefreshSuccess === 'function') {
      options.onAuthRefreshSuccess(keycloak);
    }
  };

  keycloak.onAuthRefreshError = function () {
    updateWatchVariables(false);
    if (enableLogging) {
      console.log(`${LOG_PREFIX} Auth refresh error`);
    }
    if (typeof options.onAuthRefreshError === 'function') {
      options.onAuthRefreshError(keycloak);
    }
  };

  keycloak.onAuthLogout = function () {
    updateWatchVariables(false);
    if (enableLogging) {
      console.log(`${LOG_PREFIX} Auth logout`);
    }
    if (typeof options.onAuthLogout === 'function') {
      options.onAuthLogout(keycloak);
    }
  };

  try {
    console.log(`${LOG_PREFIX} 🔍 DIAGNOSTIC: Calling keycloak.init()...`, { timestamp: new Date().toISOString() });
    const authenticated = await keycloak.init(options.init);
    console.group(`${LOG_PREFIX} 🔍 DIAGNOSTIC: keycloak.init() resolved`);
    console.log('authenticated:', authenticated);
    console.log('token exists:', !!keycloak.token);
    console.log('refreshToken exists:', !!keycloak.refreshToken);
    console.log('idToken exists:', !!keycloak.idToken);
    console.log('sessionId:', keycloak.sessionId);
    console.log('subject:', keycloak.subject);
    console.log('timeSkew:', keycloak.timeSkew);
    console.log('silentCheckSsoRedirectUri after init:', keycloak.silentCheckSsoRedirectUri);
    console.log('timestamp:', new Date().toISOString());
    console.groupEnd();
    updateWatchVariables(authenticated);
    if (enableLogging) {
      console.log(`${LOG_PREFIX} Init success, authenticated: ${authenticated}`);
    }
    if (typeof options.onInitSuccess === 'function') {
      options.onInitSuccess(authenticated);
    }
  } catch (err) {
    console.group(`${LOG_PREFIX} 🔍 DIAGNOSTIC: keycloak.init() REJECTED`);
    console.error('Error:', err);
    console.error('Error message:', err?.message);
    console.error('Error cause:', err?.cause);
    console.log('silentCheckSsoRedirectUri after error:', keycloak.silentCheckSsoRedirectUri);
    console.log('timestamp:', new Date().toISOString());
    console.groupEnd();
    updateWatchVariables(false);
    const error = new Error('Failure during initialization of keycloak-js adapter', { cause: err });
    if (typeof options.onInitError === 'function') {
      options.onInitError(error, err);
    } else {
      console.error(error, err);
    }
  }

  let updateTokenTimeout = null;

  function getRtpToken({ autoRefresh = false, refreshToken = null} = {}) {
    const rtpConfig = options.config.rtp;
    const baseUrl = options.config.url;
    const realm = options.config.realm;
    const token = keycloak.token;
    const endpoint = `${baseUrl}/realms/${realm}/protocol/openid-connect/token`;
    const requestBody = {
      grant_type: rtpConfig.grand_type,
      audience: rtpConfig.audience,
      ...refreshToken ? { refresh_token: refreshToken } : {},
    };

    return new Promise((resolve, reject) => {
      if (rtpToken && !refreshToken) {
        resolve(rtpToken)
        return;
      }

      axios.post(endpoint, qs.stringify(requestBody), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }).then((response) => {
        rtpToken = response?.data?.access_token;
        const refreshInterval = response?.data?.expires_in * 1000 * 0.8;

        if (autoRefresh) {
          if (updateTokenTimeout) clearTimeout(updateTokenTimeout);
          updateTokenTimeout = setTimeout(async () => {
            await getRtpToken({ autoRefresh: true, refreshToken: response.data.refresh_token });
          }, refreshInterval);
        }
        updateWatchVariables(true);
        resolve(rtpToken);
      }).catch((error) => {
        reject(error);
      });
    });
  }

  async function loginFn(loginOptions) {
    try {
      await keycloak.login(loginOptions);
      store.dispatch('auth/setKeycloak', keycloak);
      store.dispatch('auth/authLogin', keycloak.authenticated);
    } catch (err) {
      console.error(`${LOG_PREFIX} Error keycloak login: ${JSON.stringify(err)}`);
    }
  }

  function logoutFn(logoutOptions) {
    keycloak.logout(logoutOptions);
  }

  function updateWatchVariables(isAuthenticated = false) {
    watch.authenticated = isAuthenticated;
    watch.loginFn = loginFn;
    watch.login = keycloak.login;
    watch.createLoginUrl = keycloak.createLoginUrl;
    watch.logoutFn = logoutFn;
    watch.logout = keycloak.logout;
    watch.createLogoutUrl = keycloak.createLogoutUrl;
    watch.createRegisterUrl = keycloak.createRegisterUrl;
    watch.register = keycloak.register;
    watch.keycloak = keycloak;
    if (isAuthenticated) {
      watch.accountManagement = keycloak.accountManagement;
      watch.createAccountUrl = keycloak.createAccountUrl;
      watch.hasRealmRole = keycloak.hasRealmRole;
      watch.hasResourceRole = keycloak.hasResourceRole;
      watch.loadUserProfile = keycloak.loadUserProfile;
      watch.loadUserInfo = keycloak.loadUserInfo;
      watch.token = keycloak.token;
      watch.rtpToken = rtpToken;
      watch.subject = keycloak.subject;
      watch.idToken = keycloak.idToken;
      watch.idTokenParsed = keycloak.idTokenParsed;
      watch.realmAccess = keycloak.realmAccess;
      watch.resourceAccess = keycloak.resourceAccess;
      watch.refreshToken = keycloak.refreshToken;
      watch.refreshTokenParsed = keycloak.refreshTokenParsed;
      watch.timeSkew = keycloak.timeSkew;
      watch.responseMode = keycloak.responseMode;
      watch.responseType = keycloak.responseType;
      watch.tokenParsed = keycloak.tokenParsed;
      watch.userName = keycloak.tokenParsed.preferred_username;
      watch.fullName = keycloak.tokenParsed.name;
      watch.getRtpToken = getRtpToken
    }
  }
}

function assertOptions(options) {
  const {
    config, init, onReady, onInitError, onAuthRefreshError, onAuthLogout,
  } = options;
  if (typeof config !== 'string' && !_isObject(config)) {
    return { hasError: true, error: `'config' option must be a string or an object. Found: '${typeof config}'` };
  }
  if (!_isObject(init) || typeof init.onLoad !== 'string') {
    return { hasError: true, error: `'init' option must be an object with an 'onLoad' property. Found: '${init}'` };
  }
  if (onReady && typeof onReady !== 'function') {
    return { hasError: true, error: `'onReady' option must be a function. Found: '${typeof onReady}'` };
  }
  if (onInitError && typeof onInitError !== 'function') {
    return { hasError: true, error: `'onInitError' option must be a function. Found: '${typeof onInitError}'` };
  }
  if (onAuthRefreshError && typeof onAuthRefreshError !== 'function') {
    return { hasError: true, error: `'onAuthRefreshError' option must be a function. Found: '${typeof onAuthRefreshError}'` };
  }
  if (onAuthLogout && typeof onAuthLogout !== 'function') {
    return { hasError: true, error: `'onAuthLogout' option must be a function. Found: '${typeof onAuthLogout}'` };
  }
  return {
    hasError: false,
    error: null,
  };
}

function _isObject(obj) {
  return obj !== null && typeof obj === 'object' && Object.prototype.toString.call(obj) !== '[object Array]';
}

function getConfig(config) {
  if (_isObject(config)) return Promise.resolve(config);
  return fetch(config, { headers: { 'Accept': 'application/json' } })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.statusText}`);
      }
      return response.json();
    });
}

function sanitizeConfig(config) {
  const renameProp = (oldProp, newProp, { [oldProp]: old, ...others }) => ({
    [newProp]: old,
    ...others,
  });
  return Object.keys(config).reduce((previous, key) => {
    if (['authRealm', 'authUrl', 'authClientId'].includes(key)) {
      const cleaned = key.replace('auth', '');
      const newKey = cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
      return renameProp(key, newKey, previous);
    }
    return previous;
  }, config);
}
